import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import Alert from 'react-bootstrap/Alert'
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Table from 'react-bootstrap/Table'
import Pagination from '../../../component/Pagination'
import ApplyPanel from './ApplyPanel'
import LayoutMergeGroupCard, { REASON_LABELS, day } from './LayoutMergeGroupCard'
import useDecisions from './useDecisions'
import { decisionBody, losersOf } from './layoutMergeDraft'

// Duplicate layouts within a course.
//
// Merging courses re-points every layout of both at the survivor, so a course that was entered
// twice now lists its layouts twice. Layouts that play the same par on every hole are grouped here:
//
// - **Automatic**: identical in everything a user can see. The server has already picked the one to
//   keep -- the only played one, the most played one, or else the newest -- and the rest are hidden
//   behind it as earlier versions. Included by default; skip the odd one.
// - **Manual review**: same pars, different names or data. Nothing is included until you press
//   Merge, because being here means the layouts are *not* the same.
//
// A hidden layout is deactivated and chained to the survivor with `superseded_by`. Rounds played on
// it keep pointing at it, so every scorecard still shows exactly what was played. There is no undo
// on this page; the dry run is not optional.
//
// One batch sends the automatic groups first, then the reviewed ones. A reviewed group may keep a
// layout that an automatic group keeps too, and the server applies groups in order.
export default function LayoutMergesPass({ country, data, onDryRun, onApply, applying, dryRunSeen }) {
  const auto = useDecisions('layout-merges-auto', country)
  const manual = useDecisions('layout-merges-manual', country)
  const [previewed, setPreviewed] = useState({})
  const [autoPage, setAutoPage] = useState(0)

  const autoGroups = useMemo(() => data?.auto || [], [data])
  const manualGroups = useMemo(() => data?.manual || [], [data])

  if (!data) return null

  const autoIncluded = (group) => auto.decisions[group.groupKey] !== false
  const acceptedAuto = autoGroups.filter(autoIncluded)
  const acceptedManual = manualGroups.filter(
    (group) => manual.decisions[group.groupKey]?.accepted && losersOf(group, manual.decisions[group.groupKey]).length > 0
  )

  const batch = () => [
    ...acceptedAuto.map((group) => decisionBody(group)),
    ...acceptedManual.map((group) => decisionBody(group, manual.decisions[group.groupKey])),
  ]

  const clearAll = () => {
    auto.clear()
    manual.clear()
  }

  const submit = (dryRun) => (dryRun ? onDryRun(batch()) : onApply(batch(), clearAll))

  const submitGroup = (group, dryRun) => {
    const payload = [decisionBody(group, manual.decisions[group.groupKey])]
    if (dryRun) {
      onDryRun(payload, undefined, { silent: true })
      setPreviewed((current) => ({ ...current, [group.groupKey]: true }))
      return
    }
    onApply(
      payload,
      () => {
        manual.setDecision(group.groupKey, undefined)
        setPreviewed((current) => ({ ...current, [group.groupKey]: false }))
      },
      { silent: true }
    )
  }

  const hiddenCount =
    acceptedAuto.reduce((n, g) => n + g.members.length - 1, 0) +
    acceptedManual.reduce((n, g) => n + losersOf(g, manual.decisions[g.groupKey]).length, 0)
  const decided = acceptedAuto.length + acceptedManual.length

  const { counts } = data

  return (
    <>
      <p className="text-muted small">
        {counts.considered} active public layouts compared.{' '}
        <strong>{counts.autoGroups}</strong> identical groups ({counts.autoHidden} layouts to hide),{' '}
        <strong>{counts.manualGroups}</strong> to review.
        {counts.skippedNoHoles > 0 && ` ${counts.skippedNoHoles} layouts have no holes and were left out.`}
      </p>

      {autoGroups.length === 0 && manualGroups.length === 0 && (
        <Alert variant="light">No duplicate layouts in this country.</Alert>
      )}

      {autoGroups.length > 0 && (
        <section className="mb-4">
          <h3 className="h5">Automatic — identical duplicates</h3>
          <p className="text-muted small mb-2">
            Same pars, names and hole data. The layout marked <em>keep</em> stays visible; the others are
            hidden as its earlier versions.
          </p>
          <div className="d-flex gap-2 mb-2 align-items-center">
            <Button
              size="sm"
              variant="outline-success"
              onClick={() => autoGroups.forEach((g) => auto.setDecision(g.groupKey, undefined))}
            >
              Include all {autoGroups.length}
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => autoGroups.forEach((g) => auto.setDecision(g.groupKey, false))}
            >
              Skip all
            </Button>
            <span className="text-muted small">
              <strong>{acceptedAuto.length}</strong> of {autoGroups.length} in the batch
            </span>
          </div>
          <Pagination items={autoGroups} page={autoPage} onPage={setAutoPage} pageSize={100}>
            {(shown) => (
              <Table size="sm" responsive className="align-middle">
                <thead>
                  <tr>
                    <th>Include</th>
                    <th>Course</th>
                    <th>Holes</th>
                    <th>Keep</th>
                    <th>Hide</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((group) => {
                    const [survivor, ...hidden] = group.members
                    return (
                      <tr key={group.groupKey} className={autoIncluded(group) ? undefined : 'text-muted'}>
                        <td>
                          <Form.Check
                            type="checkbox"
                            aria-label={`Include ${group.groupKey}`}
                            checked={autoIncluded(group)}
                            onChange={(e) =>
                              auto.setDecision(group.groupKey, e.target.checked ? undefined : false)
                            }
                          />
                        </td>
                        <td>
                          <Link to={`/course/${group.courseId}`}>{group.courseName || group.courseId}</Link>
                        </td>
                        <td className="text-nowrap">
                          {group.holeCount} / par {group.par}
                        </td>
                        <td>
                          #{survivor.layoutId} {survivor.name}{' '}
                          <Badge bg="success">{REASON_LABELS[group.survivorReason] || group.survivorReason}</Badge>
                          <div className="text-muted small">
                            {survivor.games} games, updated {day(survivor.updated)}
                          </div>
                        </td>
                        <td className="small">
                          {hidden.map((m) => (
                            <div key={m.layoutId}>
                              #{m.layoutId} — {m.games} games, updated {day(m.updated)}
                            </div>
                          ))}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
            )}
          </Pagination>
        </section>
      )}

      {manualGroups.length > 0 && (
        <section>
          <h3 className="h5">Manual review — same pars, different data</h3>
          <p className="text-muted small mb-2">
            Pick the layout to keep, untick any you want left alone, and optionally take its name,
            description and directions from another one. Nothing here is merged until you press{' '}
            <strong>Merge</strong>.
          </p>
          {manualGroups.map((group) => (
            <LayoutMergeGroupCard
              key={group.groupKey}
              group={group}
              choice={manual.decisions[group.groupKey]}
              onChoice={(choice) => manual.setDecision(group.groupKey, choice)}
              onPreview={() => submitGroup(group, true)}
              onMerge={() => submitGroup(group, false)}
              previewed={!!previewed[group.groupKey]}
              applying={applying}
            />
          ))}
        </section>
      )}

      <ApplyPanel
        decidedCount={decided}
        total={autoGroups.length + manualGroups.length}
        dryRunSeen={dryRunSeen}
        applying={applying}
        destructive
        applyLabel={`Hide ${hiddenCount} layouts in ${decided} groups — this cannot be undone`}
        onDryRun={() => submit(true)}
        onApply={() => submit(false)}
      />
    </>
  )
}
